import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import {
  CmdInput,
  CmdRadioButton,
  CmdSwitch,
  CmdTooltip,
} from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import { transformErrors } from '../../Services/HookFormService';
import { IRidePlayerSpeedLimitsSettings } from './types';
import {
  enableArrowsAtom,
  enableSpeedlimitsAtom,
  enableWaitingsAtom,
  speedlimitSettingsAtom,
} from './RidePlayerState';
import useAnalytics from '../../hooks/useAnalytics';

const FIELD_MINIMUM_SPEED = 'minimumSpeed';
const FIELD_WARNING_SPEED = 'warningSpeed';
const FIELD_MAXIMUM_SPEED = 'maximumSpeed';
const MIMIMUM_SPEED_VALUE = 1;
const MAXIMUM_SPEED_VALUE = 500;
const FIELD_ENABLE_ARROWS = 'enableArrows';
const FIELD_ENABLE_SPEEDLIMITS = 'enableSpeedlimits';
const FIELD_ENABLE_WAITINGS = 'enableWaitings';

interface IRidePlayerMenu {
  minimumSpeed: number;
  warningSpeed: number;
  maximumSpeed: number;
  enableArrows: string;
  enableSpeedlimits: string;
  enableWaitings: string;
}

function RidePlayerMenu() {
  const { trackEvent } = useAnalytics();
  const { formatMessage: f } = useIntl();
  const [speedlimits, setSpeedLimits] =
    useRecoilState<IRidePlayerSpeedLimitsSettings>(speedlimitSettingsAtom);
  const [enableArrows, setEnableArrows] = useRecoilState(enableArrowsAtom);
  const [enableSpeedlimits, setEnableSpeedlimits] = useRecoilState(enableSpeedlimitsAtom);
  const [enableWaitings, setEnableWaitings] = useRecoilState(enableWaitingsAtom);

  const handleValidate = (values) => {
    const errors: any = {};

    if (!values.minimumSpeed) {
      errors.FIELD_MINIMUM_SPEED = `${f({ id: 'waypoints.edit.title.required' })}, ${f({
        id: 'ride.menu.speedlimit.validation',
      })}`;
    }
    if (!values.warningSpeed) {
      errors.warningSpeed = `${f({ id: 'waypoints.edit.title.required' })}, ${f({
        id: 'ride.menu.speedlimit.validation',
      })}`;
    }
    if (!values.maximumSpeed) {
      errors.maximumSpeed = `${f({ id: 'waypoints.edit.title.required' })}, ${f({
        id: 'ride.menu.speedlimit.validation',
      })}`;
    }

    if (
      Number(values.minimumSpeed) < MIMIMUM_SPEED_VALUE ||
      Number(values.minimumSpeed) >= Number(values.warningSpeed) ||
      Number(values.minimumSpeed) >= Number(values.maximumSpeed)
    ) {
      errors.minimumSpeed = `${f({
        id: 'ride.menu.speedlimit.validation.minimumspeed',
      })}`;
    }
    if (
      Number(values.warningSpeed) <= Number(values.minimumSpeed) ||
      Number(values.warningSpeed) >= Number(values.maximumSpeed)
    ) {
      errors.warningSpeed = `${f({
        id: 'ride.menu.speedlimit.validation.warningspeed',
      })}`;
    }

    if (
      Number(values.maximumSpeed) <= Number(values.minimumSpeed) ||
      Number(values.maximumSpeed) <= Number(values.warningSpeed) ||
      Number(values.maximumSpeed) >= MAXIMUM_SPEED_VALUE
    ) {
      errors.maximumSpeed = `${f({
        id: 'ride.menu.speedlimit.validation.maximumspeed',
      })}`;
    }

    if (
      Number(values.maximumSpeed) > Number(values.warningSpeed) &&
      Number(values.warningSpeed) > Number(values.minimumSpeed) &&
      Number(values.minimumSpeed) >= MIMIMUM_SPEED_VALUE &&
      Number(values.maximumSpeed) < MAXIMUM_SPEED_VALUE
    ) {
      setSpeedLimits({
        minimumSpeed: Number(values.minimumSpeed),
        warningSpeed: Number(values.warningSpeed),
        maximumSpeed: Number(values.maximumSpeed),
      });
    }
    return errors;
  };

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm<IRidePlayerMenu>({
    mode: 'onChange',
    defaultValues: {
      minimumSpeed: speedlimits.minimumSpeed,
      warningSpeed: speedlimits.warningSpeed,
      maximumSpeed: speedlimits.maximumSpeed,
      enableArrows: enableArrows ? '1' : '0',
      enableSpeedlimits: enableSpeedlimits ? '1' : '0',
      enableWaitings: enableWaitings ? '1' : '0',
    },
    resolver: async (data) => {
      const formErrors = transformErrors(handleValidate(data));
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const onSubmit = () => {
    const values = watch();
    if (
      Number(values.maximumSpeed) > Number(values.warningSpeed) &&
      Number(values.warningSpeed) > Number(values.minimumSpeed) &&
      Number(values.minimumSpeed) >= MIMIMUM_SPEED_VALUE &&
      Number(values.maximumSpeed) < MAXIMUM_SPEED_VALUE
    ) {
      const speedLimitsSettings = {
        minimumSpeed: Number(values.minimumSpeed),
        warningSpeed: Number(values.warningSpeed),
        maximumSpeed: Number(values.maximumSpeed),
      };
      setSpeedLimits(speedLimitsSettings);
      setEnableArrows(values.enableArrows === '1');
      setEnableSpeedlimits(values.enableSpeedlimits === '1');
      setEnableWaitings(values.enableWaitings === '1');
      // Saving settings is in useEffect hook in index.tsx
    }
  };

  const handleOnChange = (inputId: string, value: number) => {
    if (
      Number(watch('maximumSpeed')) > Number(watch('warningSpeed')) &&
      Number(watch('warningSpeed')) > Number(watch('minimumSpeed')) &&
      Number(value) >= MIMIMUM_SPEED_VALUE &&
      Number(value) < MAXIMUM_SPEED_VALUE
    ) {
      setSpeedLimits({
        ...speedlimits,
        [inputId]: value,
      });
    }

    trigger();
    handleSubmit(onSubmit);
  };

  return (
    <form>
      <div className="w-mc-player active">
        <div className="font-weight-bold">
          {f({
            id: 'ride.menu.drawride.title',
          })}
        </div>
        <div className="my-2">
          <div className="e-radio">
            <Controller
              name={FIELD_ENABLE_ARROWS}
              control={control}
              render={({ field }) => (
                <CmdRadioButton
                  {...field}
                  id="line-and-arrows"
                  label=""
                  options={[
                    { value: '0', item: f({ id: 'ride.menu.drawride.showline' }) },
                    {
                      value: '1',
                      item: f({ id: 'ride.menu.drawride.showlineandarrow' }),
                    },
                  ]}
                  setValue={(value) => {
                    setValue(FIELD_ENABLE_ARROWS, value);
                    setEnableArrows(value === '1');
                    if (value === '0') {
                      trackEvent(['Ride Player settings menu', 'click', 'Show line only']);
                    }
                    if (value === '1') {
                      trackEvent(['Ride Player settings menu', 'click', 'Show line and arrows']);
                    }
                    trigger(FIELD_ENABLE_ARROWS);
                  }}
                  error={errors[FIELD_ENABLE_ARROWS]?.message}
                  touched={true}
                  rowOrRows="rows"
                />
              )}
            />
          </div>
        </div>
        <div
          className="row no-gutters align-items-center mt-4"
          style={{ position: 'relative', marginBottom: '35px' }}
        >
          <div style={{ position: 'absolute' }}>
            <CmdTooltip
              content={
                <div>
                  {f({
                    id: 'ride.menu.drawride.showstops.tooltip',
                  })}
                </div>
              }
              width={150}
            >
              <div className="font-weight-bold">
                {f({
                  id: 'ride.menu.drawride.showstops',
                })}
              </div>
            </CmdTooltip>
          </div>
          <div
            className="col-auto e-checkbox-switch e-checkbox-switch--sm"
            style={{ position: 'absolute', right: 0, bottom: '-12px' }}
          >
            <Controller
              name={FIELD_ENABLE_WAITINGS}
              control={control}
              render={({ field }) => (
                <CmdSwitch
                  {...field}
                  label=""
                  id={FIELD_ENABLE_WAITINGS}
                  value={watch('enableWaitings')}
                  setValue={(value) => {
                    setValue(FIELD_ENABLE_WAITINGS, value);
                    trigger(FIELD_ENABLE_WAITINGS);
                    setEnableWaitings(value === '1');
                    if (value === '0') {
                      trackEvent(['Ride Player settings menu', 'click', 'Show stops disabled']);
                    }
                    if (value === '1') {
                      trackEvent(['Ride Player settings menu', 'click', 'Show stops enabled']);
                    }
                  }}
                  error={errors[FIELD_ENABLE_WAITINGS]?.message}
                  touched={true}
                />
              )}
            />
          </div>
        </div>
        <div
          className="row no-gutter align-items-center mt-4"
          style={{ position: 'relative', marginBottom: '35px' }}
        >
          <div className="col" style={{ position: 'absolute' }}>
            <CmdTooltip
              content={
                <div>
                  {f({
                    id: 'ride.menu.speedlimit.title.tooltip',
                  })}
                </div>
              }
              width={150}
            >
              <div className="font-weight-bold">{f({ id: 'ride.menu.speedlimit.title' })}</div>
            </CmdTooltip>
          </div>
          <div
            className="col-auto e-checkbox-switch e-checkbox-switch--sm"
            style={{ position: 'absolute', right: 0, bottom: '-12px' }}
          >
            <Controller
              name={FIELD_ENABLE_SPEEDLIMITS}
              control={control}
              render={({ field }) => (
                <CmdSwitch
                  {...field}
                  label=""
                  id={FIELD_ENABLE_SPEEDLIMITS}
                  value={watch('enableSpeedlimits')}
                  setValue={(value) => {
                    if (value === '0') {
                      trackEvent(['Ride Player settings menu', 'click', 'Speed limits disabled']);
                    } else {
                      trackEvent(['Ride Player settings menu', 'click', 'Speed limits enabled']);
                    }
                    setValue(FIELD_ENABLE_SPEEDLIMITS, value);
                    trigger(FIELD_ENABLE_SPEEDLIMITS);
                    setEnableSpeedlimits(value === '1');
                  }}
                  error={errors[FIELD_ENABLE_SPEEDLIMITS]?.message}
                  touched={true}
                />
              )}
            />
          </div>
        </div>
        <div className="row no-gutters">
          <div className="col-auto pl-2">
            <div className="e-speed-limits-vertical">
              <div className="item green" />
              <div className="item green">
                <CmdTooltip
                  content={
                    <div style={{ fontWeight: 'normal' }}>
                      {f({
                        id: 'ride.menu.speedlimit.green.tooltip',
                      })}
                    </div>
                  }
                  width={150}
                >
                  {speedlimits.minimumSpeed}
                </CmdTooltip>
              </div>
              <div className="item yellow">
                <CmdTooltip
                  content={
                    <div style={{ fontWeight: 'normal' }}>
                      {f({
                        id: 'ride.menu.speedlimit.yellow.tooltip',
                      })}
                    </div>
                  }
                  width={150}
                >
                  {speedlimits.warningSpeed}
                </CmdTooltip>
              </div>
              <div className="item orange">
                <CmdTooltip
                  content={
                    <div style={{ fontWeight: 'normal' }}>
                      {f({
                        id: 'ride.menu.speedlimit.orange.tooltip',
                      })}
                    </div>
                  }
                  width={150}
                >
                  {speedlimits.maximumSpeed}
                </CmdTooltip>
              </div>
              <div className="item red" />
            </div>
          </div>
          <div className="col mt-6 pl-2">
            <div className={errors.minimumSpeed ? 'form-group-error' : ''}>
              <Controller
                name={FIELD_MINIMUM_SPEED}
                control={control}
                render={({ field }) => (
                  <CmdInput
                    {...field}
                    label=""
                    id={FIELD_MINIMUM_SPEED}
                    type="number"
                    placeholder={f({ id: 'ride.menu.speedlimit.placeholder' })}
                    onChangeCallback={(value) => {
                      setValue(FIELD_MINIMUM_SPEED, value);
                      trackEvent(['Ride Player settings menu', 'change', 'Minimum speed', value]);
                      handleOnChange(FIELD_MINIMUM_SPEED, value);
                    }}
                    error={errors.minimumSpeed?.message}
                    touched
                  />
                )}
              />
            </div>
            <div className={errors.warningSpeed ? 'form-group-error' : ''}>
              <Controller
                name={FIELD_WARNING_SPEED}
                control={control}
                render={({ field }) => (
                  <CmdInput
                    {...field}
                    label=""
                    id={FIELD_WARNING_SPEED}
                    type="number"
                    placeholder={f({ id: 'ride.menu.speedlimit.placeholder' })}
                    onChangeCallback={(value) => {
                      setValue(FIELD_WARNING_SPEED, value);
                      trackEvent(['Ride Player settings menu', 'change', 'Warning speed', value]);
                      handleOnChange(FIELD_WARNING_SPEED, value);
                    }}
                    error={errors.warningSpeed?.message}
                    touched
                  />
                )}
              />
            </div>
            <div className={errors.maximumSpeed ? 'form-group-error' : ''}>
              <Controller
                name={FIELD_MAXIMUM_SPEED}
                control={control}
                render={({ field }) => (
                  <CmdInput
                    {...field}
                    id={FIELD_MAXIMUM_SPEED}
                    label=""
                    type="number"
                    placeholder={f({ id: 'ride.menu.speedlimit.placeholder' })}
                    onChangeCallback={(value) => {
                      setValue(FIELD_MAXIMUM_SPEED, value);
                      trackEvent(['Ride Player settings menu', 'change', 'Maximum speed', value]);
                      handleOnChange(FIELD_MAXIMUM_SPEED, value);
                    }}
                    error={errors.maximumSpeed?.message}
                    touched
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

export default React.memo(RidePlayerMenu);
